import React from 'react';
import { Sparkles, Loader2, FileText, Check, MessageSquare } from 'lucide-react';
import { ScriptDropZone } from '@/components/video-editor/ScriptDropZone';
import { PlanModeChat } from '@/components/video-editor/PlanModeChat';
import { ScriptAnalysisChat } from '@/components/video-editor/ScriptAnalysisChat';
import type { useScriptAnalysis } from '@/hooks/useScriptAnalysis';
import type { usePlanMode } from '@/hooks/usePlanMode';
import type { EnhancerFile } from '@/lib/enhancerMeta';

// AI Create panel — creative brief + script drop zone + Generate, with the
// conversational refinement surfaces (Plan Mode chat, script-analysis chat).

interface AICreatePanelProps {
  files: EnhancerFile[];
  includedCount: number;
  creativeBrief: string;
  onCreativeBriefChange: (brief: string) => void;
  generatedPrompt: string;
  script: ReturnType<typeof useScriptAnalysis>;
  plan: ReturnType<typeof usePlanMode>;
  aspectRatio: string;
  onAspectRatioChange: (ratio: string) => void;
  isGenerating: boolean;
  onGenerate: () => void;
  hasTimelineEdit: boolean;
}

const cardStyle: React.CSSProperties = {
  padding: 18,
  borderRadius: 'var(--hj-r-md)',
  background: 'var(--hj-surface-1)',
  border: '1px solid var(--hj-hairline)',
};

export const AICreatePanel: React.FC<AICreatePanelProps> = ({
  files,
  includedCount,
  creativeBrief,
  onCreativeBriefChange,
  generatedPrompt,
  script,
  plan,
  aspectRatio,
  onAspectRatioChange,
  isGenerating,
  onGenerate,
  hasTimelineEdit,
}) => {
  return (
    <div className="flex flex-col gap-4">
      {/* Creative brief */}
      <section style={cardStyle}>
        <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
          <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Creative brief</span>
        </div>
        <textarea
          value={creativeBrief}
          onChange={(e) => onCreativeBriefChange(e.target.value)}
          rows={4}
          placeholder={generatedPrompt || 'Tell the AI what to make — tone, pacing, what to keep, what to cut…'}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid var(--hj-hairline)',
            background: 'var(--hj-surface-2)',
            color: 'var(--hj-ink)',
            fontSize: 13.5,
            lineHeight: 1.5,
            fontFamily: 'inherit',
            resize: 'vertical',
          }}
        />
        <p className="m-0" style={{ marginTop: 8, fontSize: 12, color: 'var(--hj-ink-3)', lineHeight: 1.45 }}>
          Leave blank to use the auto-generated brief from your assets and script.
        </p>

        <div className="flex flex-wrap items-center gap-3" style={{ marginTop: 14 }}>
          <button
            type="button"
            onClick={onGenerate}
            disabled={isGenerating || includedCount === 0}
            className="inline-flex items-center gap-2"
            style={{
              padding: '10px 22px',
              borderRadius: 999,
              background: 'var(--hj-blue)',
              border: '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)',
              fontSize: 13.5,
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: isGenerating || includedCount === 0 ? 'default' : 'pointer',
              opacity: isGenerating || includedCount === 0 ? 0.6 : 1,
            }}
          >
            {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {isGenerating ? 'Generating edit…' : hasTimelineEdit ? 'Regenerate edit' : 'Generate edit'}
          </button>
          <span style={{ fontSize: 12, color: 'var(--hj-ink-2)' }}>
            {includedCount === 0
              ? 'Add and check assets to include them.'
              : `${includedCount} asset${includedCount !== 1 ? 's' : ''} included`}
          </span>
        </div>
      </section>

      {/* Script */}
      <section style={cardStyle}>
        <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
          <FileText className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-3)' }} />
          <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Script (optional)</span>
          {script.scriptContent && <Check className="w-3.5 h-3.5" style={{ color: 'var(--hj-yellow-ink)' }} />}
        </div>
        <ScriptDropZone
          onScriptLoaded={script.handleScriptLoaded}
          onAnalyze={script.handleAnalyzeScript}
          scriptContent={script.scriptContent}
          scriptFileName={script.scriptFileName}
          isAnalyzing={script.isAnalyzingScript}
          assetsCount={files.length}
        />
      </section>

      {/* Script analysis chat (match footage to script) */}
      {script.scriptAnalysis && script.showScriptPanel && !plan.planModeActive && (
        <ScriptAnalysisChat
          analysis={script.scriptAnalysis as any}
          answers={script.scriptAnswers}
          onAnswerChange={script.handleScriptAnswerChange}
          onGeneratePlan={script.handleGenerateScriptPlan}
          isGenerating={script.isGeneratingScriptPlan}
          onClose={() => script.setShowScriptPanel(false)}
          conversation={script.scriptConversation}
          onSendMessage={script.handleScriptRefinement}
          isRefining={script.isRefiningScript}
        />
      )}

      {/* Refine with AI (Plan Mode) */}
      {plan.planModeActive ? (
        <PlanModeChat
          conversation={plan.planConversation}
          questions={plan.planQuestions}
          editPlan={plan.editPlan as any}
          isProcessing={plan.isPlanProcessing}
          isPlanComplete={plan.editPlan !== null}
          isRendering={plan.isPlanRendering}
          scriptContent={script.scriptContent}
          scriptFileName={script.scriptFileName}
          finishingTouches={plan.finishingTouches}
          files={files as any}
          onSendMessage={plan.handlePlanMessage}
          onQuestionSelect={plan.handlePlanQuestionSelect}
          onApproveAndRender={plan.handleApprovePlan}
          onRefinePlan={plan.handleRefinePlan}
          onScriptUpload={script.handleScriptLoaded}
          onFinishingTouchesChange={plan.setFinishingTouches}
          aspectRatio={aspectRatio}
          onAspectRatioChange={onAspectRatioChange}
          onClose={plan.handleClosePlanMode}
        />
      ) : (
        <section style={cardStyle}>
          <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
            <MessageSquare className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-3)' }} />
            <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Refine with AI</span>
          </div>
          <p className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.5, maxWidth: 520 }}>
            Talk through the edit with the AI editor — it asks questions, builds a plan you can
            adjust clip-by-clip, and renders when you approve.
          </p>
          <button
            type="button"
            onClick={plan.handleStartPlanMode}
            disabled={plan.isPlanProcessing || files.length === 0}
            className="inline-flex items-center gap-2"
            style={{
              marginTop: 12,
              padding: '8px 18px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: plan.isPlanProcessing || files.length === 0 ? 'default' : 'pointer',
              opacity: files.length === 0 ? 0.6 : 1,
            }}
          >
            {plan.isPlanProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
            Start AI editor chat
          </button>
        </section>
      )}
    </div>
  );
};
